/**
 * InstallerReimbursementPostingBridge
 * ------------------------------------
 * Sweeps paid installer payouts carrying a field-material reimbursement
 * claim (installer_payout_invoices.field_material_amount) and posts a WIP
 * journal entry for each (ruleInstallerFieldMaterialReimbursement).
 *
 * Structured like ProcurementPostingBridge — not AutoPostingBridge's
 * payRunRecords sweep, which reads from an in-memory stub that's always
 * empty. The data here is genuinely Supabase-backed (installer_payouts /
 * installer_payout_invoices), fetched via a dedicated service-role edge
 * function (list-paid-payouts-for-gl) since RLS blocks direct reads for
 * Koredor staff sessions and there's no realtime push for "payout just went
 * paid" — polling is the only option.
 */
import { useEffect, useRef, useState } from "react";
import { useAppData } from "@/data/AppDataContext";
import { useGeneralLedger } from "./GeneralLedgerContext";
import { ruleInstallerFieldMaterialReimbursement } from "./postingRules";
import { InstallerPayoutsService, type PaidReimbursementItem } from "@/services/installerPayouts.service";

const key = (payoutId: string) => `installer_reimbursement::${payoutId}`;
/** No realtime push for installer_payouts status changes — poll for newly-paid payouts. */
const POLL_INTERVAL_MS = 3 * 60 * 1000;

export function InstallerReimbursementPostingBridge() {
  const { jobs } = useAppData();
  const { journalEntries, postJournal } = useGeneralLedger();
  const [items, setItems] = useState<PaidReimbursementItem[]>([]);
  const postedRef = useRef<Set<string>>(new Set());

  // Keep posted-set in sync with the GL on every JE mutation.
  useEffect(() => {
    for (const je of journalEntries) {
      if (je.source === "installer_reimbursement" && je.sourceRef) {
        postedRef.current.add(key(je.sourceRef.id));
      }
    }
  }, [journalEntries]);

  // Poll for newly-paid payouts.
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      const { data } = await InstallerPayoutsService.listPaidForGL();
      if (!cancelled) setItems(data);
    };
    void load();
    const interval = setInterval(load, POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, []);

  useEffect(() => {
    for (const item of items) {
      const k = key(item.payout.id);
      if (postedRef.current.has(k)) continue;
      const job = jobs.find((j) => j.id === item.payout.execution_id);
      const rule = ruleInstallerFieldMaterialReimbursement(
        item.payout,
        item.invoice.field_material_amount,
        job,
      );
      if (!rule) continue;
      try {
        postJournal(rule);
        postedRef.current.add(k);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn(
          `[InstallerReimbursementPostingBridge] payout ${item.payout.id}:`,
          e instanceof Error ? e.message : e,
        );
      }
    }
  }, [items, jobs, postJournal]);

  return null;
}
